import { useState } from 'react'
import type { MissionDto } from '@/api/hooks'
import { CombatReplay } from '@/components/CombatReplay'
import { ResourceTally } from '@/components/game/ui'
import { StatIcon, SkullIcon } from '@/components/game/GameIcons'

type AmbushDto = NonNullable<NonNullable<MissionDto['payload']['result']>['ambush']>
type InterceptionDto = NonNullable<NonNullable<MissionDto['payload']['result']>['interception']>

/**
 * Report section for a sea-ambush (commerce) or a naval interception
 * (contrebande/marché noir) — Mer dangereuse. Unlike a raid or a PvP report,
 * the PLAYER IS THE DEFENDER here (the NPC attacks the convoy on its return
 * leg), so {@link CombatReplay} is always fed `perspective="defender"`.
 *
 * Renders nothing if the mission payload carries neither `ambush` nor
 * `interception` — safe to mount unconditionally from the mission card.
 */
export function SeaBattleReport({ ambush, interception }: { ambush?: AmbushDto; interception?: InterceptionDto }) {
  if (ambush) return <AmbushReport ambush={ambush} />
  if (interception) return <InterceptionReport interception={interception} />
  return null
}

function AmbushReport({ ambush }: { ambush: AmbushDto }) {
  const [showReplay, setShowReplay] = useState(false)
  const repelled = ambush.outcome === 'repelled'
  const tone = repelled ? 'text-emerald-300' : 'text-red-400'
  const heading = repelled ? '⚔️ Embuscade · repoussée' : '⚔️ Embuscade · défaite'
  const crippled = crippledSurvivorNames(ambush.combat.defenderSurvivors)

  return (
    <div className="rounded-md border border-sand-500/15 bg-ocean-900/50 px-2.5 py-2 space-y-1.5">
      <p className={`text-sm font-semibold ${tone}`}>{heading}</p>

      {repelled ? (
        ambush.wreckPrizeGold > 0 ? (
          <p className="text-xs text-ocean-200 flex items-center gap-1.5 flex-wrap">
            <span className="text-ocean-300">Prime d'épaves</span>
            <ResourceTally gold={ambush.wreckPrizeGold} className="text-sand-200 font-semibold" />
          </p>
        ) : null
      ) : (
        <p className="text-xs text-red-300">Cargaison perdue.</p>
      )}

      <LostShipsLine lostShips={ambush.lostShips} />
      <CrippledLine names={crippled} />
      <ReplayButton onClick={() => setShowReplay(true)} />

      {showReplay && (
        <CombatReplay combat={ambush.combat} perspective="defender" onClose={() => setShowReplay(false)} />
      )}
    </div>
  )
}

function InterceptionReport({ interception }: { interception: InterceptionDto }) {
  const [showReplay, setShowReplay] = useState(false)
  const escaped = interception.outcome === 'escaped'
  const tone = escaped ? 'text-emerald-300' : 'text-red-400'
  const heading = escaped ? '🚨 Interception · blocus forcé' : '🚨 Interception · cargaison confisquée'
  const crippled = crippledSurvivorNames(interception.combat.defenderSurvivors)

  return (
    <div className="rounded-md border border-sand-500/15 bg-ocean-900/50 px-2.5 py-2 space-y-1.5">
      <p className={`text-sm font-semibold ${tone}`}>{heading}</p>

      <p className={`text-xs ${escaped ? 'text-ocean-200' : 'text-red-300'}`}>
        {escaped ? 'Blocus forcé, cargaison vendue/livrée.' : 'Cargaison saisie.'}
      </p>

      <LostShipsLine lostShips={interception.lostShips} />
      <CrippledLine names={crippled} />
      <ReplayButton onClick={() => setShowReplay(true)} />

      {showReplay && (
        <CombatReplay combat={interception.combat} perspective="defender" onClose={() => setShowReplay(false)} />
      )}
    </div>
  )
}

function LostShipsLine({ lostShips }: { lostShips: string[] }) {
  if (lostShips.length === 0) return null
  return (
    <p className="text-xs text-rum-300 flex items-start gap-1.5">
      <SkullIcon className="w-4 h-4 shrink-0 mt-0.5" />
      <span>
        <strong className="text-rum-200">Navire(s) perdu(s)</strong> : {lostShips.join(', ')}
      </span>
    </p>
  )
}

function CrippledLine({ names }: { names: string[] }) {
  if (names.length === 0) return null
  return (
    <p className="text-[11px] text-ocean-300 italic">
      {names.join(', ')} rentre{names.length > 1 ? 'nt' : ''} au port en piteux état.
    </p>
  )
}

function ReplayButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="text-xs px-3 py-1.5 rounded-md bg-rum-500/80 text-ocean-50 font-semibold hover:bg-rum-500 transition-colors inline-flex items-center gap-1.5"
    >
      <StatIcon name="abordage" className="w-3.5 h-3.5" />
      Revoir la bataille
    </button>
  )
}

/** Names of the player's (defender) ships that survived but took damage — the "limp home" note. */
function crippledSurvivorNames(defenderSurvivors: { name: string; hp: number; maxHp: number; sunk: boolean }[]): string[] {
  return defenderSurvivors.filter((s) => !s.sunk && s.hp < s.maxHp).map((s) => s.name)
}
